<template>

    <transition :name="transitionData.name" :duration="transitionData.duration" @after-enter="onAfterEnter">

        <div v-show="isVisible"
             class="dmb-widget" :class="resolvedClasses" :style="resolvedStyle"
             @mousedown.left.stop="onWidgetDown">

            <div class="widget-inner" :style="resolvedInnerStyle">

                <dmb-element-background
                    :freeze="freeze"
                    :asset-id="widget.style.backgroundAssetId"
                    :color="widget.style.backgroundColor"
                    :content-mode="widget.style.contentMode"
                    :repeat="false"
                ></dmb-element-background>

                <div class="widget-content">
                    <component v-if="definition.contentComponent" :is="definition.contentComponent"
                               :widget="widget" :freeze="freeze" :visible="widgetIsVisible"/>
                </div>

                <slot></slot>

            </div>

            <div class="widget-handle" v-if="selected && (movable || resizable)">
                <template v-if="resizable">
                    <div class="handle" v-for="handle in handles" :key="handle" draggable="false"
                         :class="[handle]" @mousedown.stop="onHandleDown(handle, $event)" @dragstart.prevent></div>
                </template>
            </div>

        </div>

    </transition>

</template>

<script>

import { Vue, Options } from 'vue-class-component'

import { mapValues, clone } from 'lodash'

import { ResizeMode, TransformHandle } from '../../enum/Transform';
import { getWidgetDefinition } from '../../widget';
import { generateCustomAnimationCss, getAnimationClass, resolveWidgetStyle } from '../../util/style';
import AnimationStage from '../../enum/AnimationStage';

import ElementBackground from '../ui/ElementBackground.vue';
import AnimationType from '../../enum/AnimationType';
import SnapPosition from '../../enum/SnapPosition';


export default Options({
    name: 'dmb-widget',
    props: {
        widget: Object,
        freeze: Boolean,
        selected: Boolean,
        movable: Boolean,
        resizable: Boolean,
        dimmed: Boolean,
        startPosition: Object,
        scale: Number,
        slideDuration: Number,
        slidePending: Boolean,
        slideVisible: {
            type: Boolean,
            default: true
        },
        getMovementLock: {
            type: Function,
            default: () => true
        }
    },
    components: {
        [ElementBackground.name]: ElementBackground
    }
})(
    class Widget extends Vue {
        position = null;

        activeAnimationStage = null;
        isVisible = false;

        created(){

            this.logger = this.logger.child(this.widget.id);

            this.loadPosition();

            this.$watch('widget', this.loadPosition, { deep: true });

            this.$watch('slideVisible', this.updateAnimations);
            this.$watch('slidePending', this.updateAnimations);

            this.updateAnimations();
        }

        updateAnimations(){

            if(this.freeze){
                this.isVisible = true;
                return;
            }

            // TODO: Widgets re-appear on slide out transition
            const inAnimation = this.getAnimation(AnimationStage.IN);
            const outAnimation = this.getAnimation(AnimationStage.OUT);
            const loopAnimation = this.getAnimation(AnimationStage.LOOP);

            if(outAnimation && this.slidePending && !this.slideVisible){
                this.queueOutAnimation();
            }

            if(inAnimation && this.activeAnimationStage !== AnimationStage.IN && this.slidePending){

                if(inAnimation.type === AnimationType.A_B){
                    this.$nextTick(() => this.queueInAnimation());
                }
                else if(this.slideVisible){
                    this.queueInAnimation();
                }
            }
            else if(!inAnimation && !this.activeAnimationStage && this.slidePending){

                this.isVisible = true;

                if(loopAnimation){
                    this.queueLoopAnimation();
                }
            }
            else if(!this.slideVisible && !this.slidePending){

                this.activeAnimationStage = null;
                this.isVisible = false;
            }
        }

        mounted(){

            if(!this.freeze){
                this.insertAnimationStyles();
            }
        }

        unmounted(){

            if(this.dragSession){
                document.removeEventListener('mousemove', this.updateDragSession);
                document.removeEventListener('mouseup', this.endDragSession);
            }

            if(this.potentialDragSession){
                document.removeEventListener('mousemove', this.potentialDragSession.potentialStartDragSession);
                document.removeEventListener('mouseup', this.potentialDragSession.cancelPotentialDragSession);
            }

            if(this.animationStyles){

                for(let style of this.animationStyles){
                    this.$style.removeCss(style);
                }
            }
        }

        loadPosition(){

            const { positionX, positionY, width, height } = this.widget;
            const scale = this.scale || 1.0;

            if(this.startPosition){

                this.position = {
                    left: this.startPosition.x,
                    top: this.startPosition.y,
                    width: width * scale,
                    height: height * scale
                }
            }
            else {

                this.position = {
                    left: positionX,
                    top: positionY,
                    width: width * scale,
                    height: height * scale
                }
            }
        }

        onWidgetDown(originalEvent){

            if(!this.movable){
                return;
            }

            const widgetRect = this.$el.getBoundingClientRect();

            const origin = {
                x: originalEvent.pageX - widgetRect.left,
                y: originalEvent.pageY - widgetRect.top
            }

            const cancelPotentialDragSession = () => {

                document.removeEventListener('mousemove', potentialStartDragSession);
                document.removeEventListener('mouseup', cancelPotentialDragSession);

                this.potentialDragSession = null;
            }

            const potentialStartDragSession = (currentEvent) => {

                const offset = Math.max(Math.abs(originalEvent.pageX - currentEvent.pageX), Math.abs(originalEvent.pageY - currentEvent.pageY));

                if(offset >= 5){

                    cancelPotentialDragSession();

                    this.startDragSession({
                        modes: [],
                        cursor: 'move',
                        origin
                    });
                }
            };

            this.potentialDragSession = {
                cancelPotentialDragSession, potentialStartDragSession
            }

            document.addEventListener('mousemove', potentialStartDragSession);
            document.addEventListener('mouseup', cancelPotentialDragSession, { once: true });
        }

        onHandleDown(handleKey, e){

            if(!this.selected || !this.resizable){
                return;
            }

            const handle = TransformHandle[handleKey];

            const handleRect = e.currentTarget.getBoundingClientRect();
            const widgetRect = this.$el.getBoundingClientRect();

            const localHandleOrigin = {
                x: (handleRect.left + (handleRect.width / 2)) - widgetRect.left,
                y: (handleRect.top + (handleRect.height / 2)) - widgetRect.top
            }

            this.startDragSession({
                modes: handle.modes,
                cursor: handle.cursor,
                origin: localHandleOrigin
            });
        }

        startDragSession({ modes, cursor, origin }){

            const $slide = this.$el.closest('.dmb-slide');
            const slideRect = $slide.getBoundingClientRect();

            this.dragSession = {
                originalPosition: { ...this.position },
                position: { ...this.position },
                modes,
                slideRect,
                origin,
                $slide
            }

            this.$cursor.set(cursor);

            document.addEventListener('mousemove', this.updateDragSession);
            document.addEventListener('mouseup', this.endDragSession);

            this.$emit('drag:start', { dragSession: this.dragSession });
        }

        updateDragSession(e){

            const { slideRect, origin, modes, originalPosition } = this.dragSession;
            const { clientX, clientY } = e;
            const previousPosition = clone(this.position);

            const local = {
                left: clientX - slideRect.left,
                top: clientY - slideRect.top
            }

            const position = {
                left: (local.left - origin.x) / slideRect.width,
                top: (local.top - origin.y) / slideRect.height,
                width: originalPosition.width,
                height: originalPosition.height
            }

            let locks = {};

            if(this.getMovementLock){

                locks = this.getMovementLock({

                    dragSession: this.dragSession,
                    position,
                    e
                }, this.widget);
            }

            let correctedPosition = {
                left: position.left,
                top: position.top
            }

            switch(locks.vertical?.position){

                case SnapPosition.LEFT:
                    correctedPosition.left = locks.vertical.x;
                    break;

                case SnapPosition.RIGHT:
                    correctedPosition.left = locks.vertical.x - position.width;
                    break;

                case SnapPosition.CENTER:
                    correctedPosition.left = locks.vertical.x - (position.width / 2);
                    break;
            }

            switch(locks.horizontal?.position){

                case SnapPosition.TOP:
                    correctedPosition.top = locks.horizontal.y;
                    break;

                case SnapPosition.BOTTOM:
                    correctedPosition.top = locks.horizontal.y - position.height;
                    break;

                case SnapPosition.CENTER:
                    correctedPosition.top = locks.horizontal.y - (position.height / 2);
                    break;
            }

            if(modes.includes(ResizeMode.X)){

                this.position.left = locks.vertical && locks.vertical.position === SnapPosition.LEFT ? correctedPosition.left : position.left;
                this.position.width = originalPosition.width - (this.position.left - originalPosition.left);
            }

            if(modes.includes(ResizeMode.Y)){

                this.position.top = locks.horizontal && locks.horizontal.position === SnapPosition.TOP ? correctedPosition.top : position.top;
                this.position.height = originalPosition.height - (this.position.top - originalPosition.top);
            }

            if(modes.includes(ResizeMode.WIDTH)){

                const endPosX = locks.vertical && locks.vertical.position === SnapPosition.RIGHT ? locks.vertical.x : local.left / slideRect.width

                this.position.width = endPosX - this.position.left;
            }

            if(modes.includes(ResizeMode.HEIGHT)){

                const endPosY = locks.horizontal && locks.horizontal.position === SnapPosition.BOTTOM ? locks.horizontal.y : local.top / slideRect.height;

                this.position.height = endPosY - this.position.top;
            }

            if(!modes.length){

                this.position.left = correctedPosition.left;
                this.position.top = correctedPosition.top;
            }

            const minSize = this.definition.minSize;

            if(minSize){

                if(this.position.width < minSize.width){

                    this.position.width = minSize.width;
                    this.position.left = previousPosition.left;
                }

                if(this.position.height < minSize.height){

                    this.position.height = minSize.height;
                    this.position.top = previousPosition.top;
                }
            }

            this.$emit('drag:update', {

                dragSession: this.dragSession,
                position: this.position,
                e
            });
        }

        endDragSession(){

            const pos = this.position;

            this.commitUpdate({
                positionX: pos.left,
                positionY: pos.top,
                width: pos.width,
                height: pos.height
            });

            this.dragSession = null;
            this.$cursor.reset();

            document.removeEventListener('mousemove', this.updateDragSession);
            document.removeEventListener('mouseup', this.endDragSession);

            this.$emit('drag:end', { position: pos });
        }

        commitUpdate(data){

            this.$emit('update', {
                id: this.widget.id,
                data
            });
        }

        getAnimation(stage){

            return this.widget.animations.find(animation => animation.stage === stage);
        }

        onAfterEnter(){

            if(this.activeAnimationStage === AnimationStage.IN){

                this.queueLoopAnimation();
            }
            else if(this.activeAnimationStage === AnimationStage.OUT){

                this.activeAnimationStage = null;
            }
        }

        queueInAnimation(){

            const animation = this.getAnimation(AnimationStage.IN);
            if(!animation || this.inAnimationTimeout){
                return;
            }

            if(animation.delay){
                this.inAnimationTimeout = window.setTimeout(() => this.startInAnimation(), animation.delay * 1000);
            }
            else {
                this.startInAnimation();
            }
        }

        queueLoopAnimation(){

            const loopAnimation = this.getAnimation(AnimationStage.LOOP);
            if(loopAnimation){
                this.activeAnimationStage = AnimationStage.LOOP;
            }
        }

        startInAnimation(){

            this.inAnimationTimeout = null;
            this.activeAnimationStage = AnimationStage.IN;
            this.isVisible = true;
        }

        queueOutAnimation(){

            const animation = this.getAnimation(AnimationStage.OUT);
            if(!animation || this.outAnimationTimeout){
                return;
            }

            let timeout = this.slideDuration - (animation.duration * 1000);
            if(animation.delay){
                timeout -= animation.delay * 1000;
            }

            this.outAnimationTimeout = window.setTimeout(() => this.startOutAnimation(), timeout);
        }

        startOutAnimation(){

            this.outAnimationTimeout = null;
            this.activeAnimationStage = AnimationStage.OUT;
            this.isVisible = false;
        }

        insertAnimationStyles(){

            const customAnimations = this.widget.animations.filter(animation => animation.type === AnimationType.A_B);
            if(!customAnimations.length){
                return;
            }

            const styles = [];

            for(let animation of customAnimations){

                const style = this.$style.insertCss(generateCustomAnimationCss(this.widget, animation));
                styles.push(style);
            }

            this.animationStyles = styles;
        }

        get widgetIsVisible(){

            return this.isVisible && (this.slideVisible || this.slidePending);
        }

        get currentAnimation(){

            if(this.freeze){
                return null;
            }

            const inAnimation = this.getAnimation(AnimationStage.IN);
            const outAnimation = this.getAnimation(AnimationStage.OUT);
            const loopAnimation = this.getAnimation(AnimationStage.LOOP);

            if(this.activeAnimationStage === AnimationStage.IN){
                return inAnimation;
            }
            else if(this.activeAnimationStage === AnimationStage.OUT){
                return outAnimation
            }
            else if(this.activeAnimationStage === AnimationStage.LOOP){
                return loopAnimation;
            }
            else {
                return null;
            }
        }

        get transitionData(){

            let animation = this.currentAnimation;
            if(animation?.stage === AnimationStage.LOOP){
                animation = null;
            }

            return {
                name: animation ? getAnimationClass(animation) : null,
                duration: animation?.duration ? animation.duration * 1000 : null
            }
        }

        get resolvedStyle(){

            const animation = this.currentAnimation;

            const styles = {
                ...mapValues(this.position, val => (val * 100) + '%'),
                'z-index': this.startPosition?.z ?? this.widget.positionZ
            };

            if(animation){

                if(animation.stage === AnimationStage.LOOP){

                    const animationClass = getAnimationClass(animation);
                    styles['animation-name'] = animationClass ? `${animationClass}Loop` : null

                    styles['animation-iteration-count'] = 'infinite'
                }

                styles['animation-duration'] = `${animation.duration}s`;

                if(animation.stage === AnimationStage.LOOP && animation.delay){
                    styles['animation-delay'] = `${animation.delay}s`;
                }
            }

            return styles;
        }

        get resolvedInnerStyle(){

            return resolveWidgetStyle(this.widget.style, this.showStyle, this.dimmed);
        }

        get resolvedClasses(){

            let classes = [];

            if(this.definition.className){
                classes.push(this.definition.className);
            }

            if(this.selected){
                classes.push('selected');
            }

            return classes;
        }

        get handles(){

            return Object.keys(TransformHandle)
        }

        get definition(){

            return getWidgetDefinition(this.widget.type);
        }

        get showStyle(){

            return this.$store.state.style.showStyle;
        }
    }
)

</script>
<style src="../../../scss/component/widget/Widget.scss" lang="scss"></style>